// Client communications (plan workstream 5, Phase 2): templated emails and letters merged
// from the record. Pure: templates + context in, subject and body out, with the merge
// fields that had no value listed so the adviser sees the gap before sending.

export interface CommsTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}

/** Merge fields and what fills them. Keep in step with `commsContext`. */
export const MERGE_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'salutation', label: 'Greeting name (salutation, else first names)' },
  { key: 'firstName', label: 'Client first name' },
  { key: 'householdName', label: 'Household, e.g. Dave and Sarah Whitfield' },
  { key: 'reviewDue', label: 'Next review date' },
  { key: 'nextMeeting', label: 'Next booked meeting' },
  { key: 'consentDue', label: 'Fee consent due date' },
  { key: 'financialYear', label: 'Current financial year, e.g. 2026-27' },
  { key: 'adviserName', label: 'Adviser name' },
  { key: 'practiceName', label: 'Practice name' },
];

export const DEFAULT_TEMPLATES: CommsTemplate[] = [
  {
    id: 'review-invite',
    name: 'Review invitation',
    subject: 'Time for your annual review',
    body: 'Dear {{salutation}},\n\nIt is time for your annual review, due {{reviewDue}}. It is a chance to look at how your portfolio has gone, check your goals are still the right ones and make sure your plan keeps pace with any changes in your life.\n\nPlease reply with a few times that suit you over the next few weeks and we will lock one in.\n\nKind regards,\n{{adviserName}}\n{{practiceName}}',
  },
  {
    id: 'meeting-confirm',
    name: 'Meeting confirmation',
    subject: 'Confirming our meeting',
    body: 'Dear {{salutation}},\n\nThis confirms our meeting on {{nextMeeting}}. If anything has changed since we last spoke (work, health, family or property), please let me know beforehand so we can cover it.\n\nKind regards,\n{{adviserName}}',
  },
  {
    id: 'fee-consent',
    name: 'Fee consent renewal',
    subject: 'Your annual fee consent',
    body: 'Dear {{salutation}},\n\nYour ongoing fee arrangement needs your consent each year to continue. Your renewal is due {{consentDue}}. We will send the consent form for e-signature; if you have any questions about the services or fees, please call me.\n\nKind regards,\n{{adviserName}}\n{{practiceName}}',
  },
  {
    id: 'eofy',
    name: 'End of financial year reminder',
    subject: 'Before 30 June',
    body: 'Dear {{salutation}},\n\nAs {{financialYear}} draws to a close, a few things are worth checking before 30 June: super contributions and the caps, any capital gains or losses to time, and income protection premiums.\n\nIf you would like to talk any of these through, reply and we will set up a call.\n\nKind regards,\n{{adviserName}}',
  },
  {
    id: 'birthday',
    name: 'Birthday note',
    subject: 'Happy birthday',
    body: 'Dear {{firstName}},\n\nWishing you a very happy birthday from all of us at {{practiceName}}.\n\nWarm regards,\n{{adviserName}}',
  },
];

const TOKEN = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;

export interface RenderedComms {
  subject: string;
  body: string;
  /** Merge fields the template uses that had no value on the record (rendered blank). */
  missing: string[];
  /** Tokens that are not merge fields at all (a typo in a template). */
  unknown: string[];
}

export function renderTemplate(t: Pick<CommsTemplate, 'subject' | 'body'>, ctx: Record<string, string | null | undefined>): RenderedComms {
  const known = new Set(MERGE_FIELDS.map((f) => f.key));
  const missing = new Set<string>();
  const unknown = new Set<string>();
  const fill = (text: string) => text.replace(TOKEN, (_m, key: string) => {
    if (!known.has(key)) { unknown.add(key); return ''; }
    const v = ctx[key];
    if (v === null || v === undefined || v === '') { missing.add(key); return ''; }
    return v;
  });
  return { subject: fill(t.subject).trim(), body: fill(t.body), missing: [...missing], unknown: [...unknown] };
}

const dmy = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : null);

/** Australian financial year containing `today`, as "2026-27". */
export function financialYear(today: string): string {
  const y = Number(today.slice(0, 4));
  const start = Number(today.slice(5, 7)) >= 7 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export interface CommsPerson { firstName: string | null; surname: string | null; role: string }

/** "Dave and Sarah Whitfield" / "Dave Whitfield and Sarah Jones" from the adults on the household. */
export function householdName(primary: { firstName: string; surname: string }, members: CommsPerson[]): string {
  const adults = members.filter((m) => m.role !== 'dependant' && (m.firstName || m.surname));
  const partner = adults.find((m) => m.role === 'partner');
  if (!partner || !partner.firstName) return `${primary.firstName} ${primary.surname}`;
  const pSurname = partner.surname || primary.surname;
  return pSurname === primary.surname
    ? `${primary.firstName} and ${partner.firstName} ${primary.surname}`
    : `${primary.firstName} ${primary.surname} and ${partner.firstName} ${pSurname}`;
}

export function commsContext(input: {
  client: { firstName: string; surname: string; salutation: string | null; informalName: string | null; reviewDue: string | null };
  members: CommsPerson[];
  today: string;
  adviserName: string | null;
  practiceName: string | null;
  consentDue?: string | null;
  nextMeeting?: string | null;
}): Record<string, string | null> {
  const partner = input.members.find((m) => m.role === 'partner' && m.firstName);
  const greet = input.client.salutation?.trim()
    || (partner ? `${input.client.informalName || input.client.firstName} and ${partner.firstName}` : input.client.informalName || input.client.firstName);
  return {
    salutation: greet,
    firstName: input.client.informalName || input.client.firstName,
    householdName: householdName(input.client, input.members),
    reviewDue: dmy(input.client.reviewDue),
    nextMeeting: input.nextMeeting ?? null,
    consentDue: dmy(input.consentDue ?? null),
    financialYear: financialYear(input.today),
    adviserName: input.adviserName,
    practiceName: input.practiceName,
  };
}

/** mailto: link for the adviser's own mail app (nothing is sent by Client-Base). */
export function mailtoHref(to: string[], subject: string, body: string): string {
  const q = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.replace(/\r?\n/g, '\r\n'))}`;
  return `mailto:${to.filter((a) => /^[^\s@,?&]+@[^\s@,?&]+$/.test(a)).join(',')}?${q}`;
}

/** Stored templates replace the defaults when any are saved; malformed entries are dropped. */
export function mergeTemplates(stored: unknown): CommsTemplate[] {
  if (!Array.isArray(stored)) return DEFAULT_TEMPLATES;
  const out: CommsTemplate[] = [];
  const seen = new Set<string>();
  for (const raw of stored) {
    const t = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    if (typeof t.id !== 'string' || !/^[a-z0-9-]{1,40}$/.test(t.id) || seen.has(t.id)) continue;
    if (typeof t.name !== 'string' || !t.name.trim() || typeof t.subject !== 'string' || typeof t.body !== 'string') continue;
    seen.add(t.id);
    out.push({ id: t.id, name: t.name.trim().slice(0, 80), subject: t.subject.slice(0, 200), body: t.body.slice(0, 5000) });
  }
  return out.length ? out.slice(0, 30) : DEFAULT_TEMPLATES;
}
